/**
 * @copyright
 * Copyright (c) 2024 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import {
  BlackLabelSiteReviewStatusMap,
  SITE_PROMOTION_STATUS_ACTIVE,
  SitePromotionStatusName,
  SitePromotionStatusNameMap,
  SKIN_REVIEW_STATUS_APPROVED,
  SKIN_REVIEW_STATUS_PENDING,
  SkinReviewStatusName,
  SkinReviewStatusNameMap,
} from "~/features/mySites/constants";
import {
  BlackLabelSiteReviewDetails,
  MySitesSiteList,
  MySitesSiteType,
  PostbackSiteListElement,
  SiteListDetails,
  SitePromotionListDetails,
} from "~/features/mySites/types";

interface MySitesApiTransforms {
  siteListToMySitesList: (data: SiteListDetails[]) => MySitesSiteList;
  sitePromotionListToMySitesList: (data: SitePromotionListDetails[]) => MySitesSiteList;
  blackLabelSiteReviewListToMySitesList: (data: BlackLabelSiteReviewDetails[]) => MySitesSiteList;
  siteListToPostbackSiteList: (data: SiteListDetails[]) => PostbackSiteListElement[];
  sitePromotionListToPostbackSiteList: (data: SitePromotionListDetails[]) => PostbackSiteListElement[];
}

const mySitesApiTransforms: MySitesApiTransforms = {
  siteListToMySitesList: (data) =>
    data.map((site) => ({
      domain: site.name,
      url: site.url,
      note: site.note,
      payoutProgramId: site.payoutProgram.id,
      payoutProgramName: site.payoutProgram.value,
      statusId: site.skinReviewStatus.value,
      statusName: SkinReviewStatusNameMap[site.skinReviewStatus.value] ?? SkinReviewStatusName.UNKNOWN,
      isEditable: site.isSkinEditable && site.skinReviewStatus.value !== SKIN_REVIEW_STATUS_PENDING,
      type: MySitesSiteType.whiteLabel,
      siteId: site.siteId,
      reviewId: site.skinReviewId,
      newSite: site.skinReviewNewSite,
    })),
  sitePromotionListToMySitesList: (data) =>
    data.map((promotion) => ({
      domain: promotion.name.name,
      url: promotion.url,
      note: promotion.note,
      payoutProgramId: promotion.payoutProgram.id,
      payoutProgramName: promotion.payoutProgram.value,
      statusId: promotion.status.value,
      statusName: SitePromotionStatusNameMap[promotion.status.value] ?? SitePromotionStatusName.UNKNOWN,
      isEditable: false,
      type: MySitesSiteType.sitePromotion,
    })),
  blackLabelSiteReviewListToMySitesList: (data) =>
    data.map((siteReviewData) => ({
      domain: siteReviewData.domain,
      statusId: Number(
        Object.keys(BlackLabelSiteReviewStatusMap).find(
          (key) => BlackLabelSiteReviewStatusMap[Number(key)] === siteReviewData.status,
        ),
      ),
      statusName: siteReviewData.status,
      isEditable: false,
      type: MySitesSiteType.blackLabel,
    })),
  siteListToPostbackSiteList: (data) =>
    data
      .filter((site) => !site.skinReviewNewSite && site.skinReviewStatus.value === SKIN_REVIEW_STATUS_APPROVED)
      .map((site) => ({
        name: site.name,
        siteId: site.siteId,
        payoutProgram: site.payoutProgram,
      })),
  sitePromotionListToPostbackSiteList: (data) =>
    data
      .filter((promotion) => promotion.status.value === SITE_PROMOTION_STATUS_ACTIVE)
      .map((promotion) => ({
        name: promotion.name.name,
        siteId: promotion.name.siteId,
        id: promotion.id,
        payoutProgram: promotion.payoutProgram,
      })),
};

export default mySitesApiTransforms;
